// import * as Sentry from '@sentry/react'

// Sentry.init({
//   dsn: import.meta.env.VITE_SENTRY_DSN,
//   environment: import.meta.env.MODE,
//   integrations: [
//     Sentry.browserTracingIntegration(),
//     Sentry.replayIntegration(),
//   ],
//   tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
//   replaysSessionSampleRate: 0.1,
//   replaysOnErrorSampleRate: 1.0,
// })

import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { AxiosError } from 'axios'
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
// Generated Routes
import { routeTree } from '@/routeTree.gen'
import {
  ApiClientProvider,
  ApiRequestError,
  createApiClient,
} from '@workspace/react-query'
import '@workspace/ui/globals.css'
import { toast } from 'sonner'
import { handleServerError } from '@/lib/handle-server-error'
import {
  isAccessRevokedError,
  isUnauthorizedError,
  noteRequestSucceeded,
  shouldTearDownAfterUnauthorized,
  tearDownSession,
} from '@/lib/handle-session-teardown'
import { RoutePending } from '@/components/route-pending'
import { DirectionProvider } from '@/context/direction-provider'
import { FontProvider } from '@/context/font-provider'
import { ShiftDialogProvider } from '@/context/shift-dialog-provider'
import { ThemeProvider } from '@/context/theme-provider'
import { TimezoneProvider } from '@/context/timezone-provider'
import './styles/index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // eslint-disable-next-line no-console
        if (import.meta.env.DEV) console.log({ failureCount, error })

        if (failureCount >= 0 && import.meta.env.DEV) return false
        if (failureCount > 3 && import.meta.env.PROD) return false

        // Skip retry for ApiRequestError on 401/403
        if (
          error instanceof ApiRequestError &&
          [401, 403].includes(error.status)
        )
          return false

        return !(
          error instanceof AxiosError &&
          [401, 403].includes(error.response?.status ?? 0)
        )
      },
      refetchOnWindowFocus: import.meta.env.PROD,
      staleTime: 10 * 1000, // 10s
    },
    mutations: {
      onError: (error) => {
        if (isAccessRevokedError(error)) {
          void tearDownSession(queryClient)
          return
        }

        handleServerError(error)

        if (error instanceof AxiosError) {
          if (error.response?.status === 304) {
            toast.error('Content not modified!')
          }
        }
      },
    },
  },
  queryCache: new QueryCache({
    // Proof the session is alive — clears any accumulated 401 streak so an
    // isolated blip can never add up to a sign-out over time.
    onSuccess: () => {
      noteRequestSucceeded()
    },
    onError: (error) => {
      // Report all query errors to Sentry
      // Sentry.captureException(error)

      // Access revoked (offboarded / suspended member). The API 403s EVERY
      // request from this session, so leaving the shell mounted means its
      // interval pollers retry forever — the loop that exhausts the API. Tear
      // the session down instead. Checked before anything else because it must
      // win over the generic 403/500 handling below.
      if (isAccessRevokedError(error)) {
        void tearDownSession(queryClient)
        return
      }

      // 401 is the sibling loop: offboarding DELETES the user's sessions, so
      // someone already signed in 401s instead of 403ing — on the same pollers,
      // forever. Isolated 401s stay ignored (below), because the route-level
      // requireAuth() handles those on the next navigation and we don't want a
      // blip to sign anyone out. A sustained streak is different: nothing is
      // coming to rescue it, so end the session.
      if (isUnauthorizedError(error)) {
        if (shouldTearDownAfterUnauthorized()) {
          void tearDownSession(queryClient)
        }
        return
      }

      // Handle fetch-based ApiRequestError (from @workspace/react-query client)
      if (error instanceof ApiRequestError) {
        if (error.status === 500) {
          toast.error(error.message || 'Something went wrong. Please try again.')
        }
        return
      }

      // Legacy: handle AxiosError (if any code path still uses axios).
      // 401 and revoked-403 are already handled above for both error shapes.
      if (error instanceof AxiosError && error.response?.status === 500) {
        toast.error(
          error.response?.data?.message ||
            'Something went wrong. Please try again.'
        )
      }
    },
  }),
})

const apiClient = createApiClient({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8081',
  appBaseURL: import.meta.env.VITE_APP_BASE_URL || 'http://localhost:5173',
  csrfProtection: true,
})

// Create a new router instance
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  defaultPendingComponent: RoutePending,
  defaultPendingMinMs: 500,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Render the app
const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ApiClientProvider client={apiClient}>
          <ThemeProvider>
            <FontProvider>
              <DirectionProvider>
                <TimezoneProvider>
                  <ShiftDialogProvider>
                    <RouterProvider router={router} />
                  </ShiftDialogProvider>
                </TimezoneProvider>
              </DirectionProvider>
            </FontProvider>
          </ThemeProvider>
        </ApiClientProvider>
      </QueryClientProvider>
    </StrictMode>
  )
}
